import {getState, postState} from "./state";
import _ from 'lodash';
import moment from "moment";
import {AlarmPathIdTypes, AlarmTypes} from "../enums/alarmPathIdTypes";
import {convertTimeToMinutesAndSeconds} from "../utils/convertTimeToMinutesAndSeconds";
import {convertZeroToOne} from "../utils/convertZeroToOne";

const getShadowName = (id: string) => {
    const alarmType = getAlarmType(id);
    switch (alarmType) {
        case AlarmTypes.TEMPERATURE:
            return AlarmTypes.TEMPERATURE + id.replace(AlarmPathIdTypes.TEMPERATURE, '') + 'SettingsId';
        case AlarmTypes.INPUT:
            return AlarmTypes.INPUT + id.replace(AlarmPathIdTypes.INPUT, '') + 'SettingsId';
        case AlarmTypes.HUMIDITY:
            return AlarmTypes.HUMIDITY + id.replace(AlarmPathIdTypes.HUMIDITY, '') + 'SettingsId';
        default:
            return 'AlarmVoltageSettingsId';
    }
}

const getAbstractAlarm = (data: any, alarmPathId: AlarmPathIdTypes, alarmType: AlarmTypes, id?: string) => {
    const alarm = getAlarmFromResponse(data, alarmPathId, alarmType, id);
    return createAlarmObjectFromResponse(alarm, alarmType);

}
// Convert response and get correct alarm
const getAlarmFromResponse = (data: any, alarmPathId: AlarmPathIdTypes, alarmType: AlarmTypes, id?: string) => {
    const key = id ? id.replace(alarmPathId, '') : '';
    const payload = data.data
    const path = 'state.desired.' + alarmType + key + 'SettingsId';
    const reportedPath = 'state.reported.' + alarmType + key + 'SettingsId';
    return _.get(payload, path, _.get(payload, reportedPath, {}));
}
// Get correct alarm from backend response
const createAlarmObjectFromResponse = (alarm: any, alarmType: AlarmTypes) => {
    if (alarmType === AlarmTypes.INPUT) {
        return {
            isActive: _.get(alarm, 'Active', false),
            reactionTime: moment.utc(_.get(alarm, 'WSett.ReactTime', 0)*1000).format('mm:ss')
        }
    }
    return {
        isActive: _.get(alarm, 'Active', false),
        valueUp: _.get(alarm, 'WSett.valueUp', 0),
        valueDown: _.get(alarm, 'WSett.valueDown', 0),
        hysteresis: _.get(alarm, 'WSett.Hys', 0),
        reactionTime: moment.utc(_.get(alarm, 'WSett.ReactTime', 0)*1000).format('mm:ss')
    }
}
// Get alarm type based on id
const getAlarmType = (id: string): AlarmTypes => {
    if (id.includes(AlarmPathIdTypes.TEMPERATURE)) {
        return AlarmTypes.TEMPERATURE;
    } else if (id.includes(AlarmPathIdTypes.ANALOG)) {
        return AlarmTypes.ANALOG;
    } else if (id.includes(AlarmPathIdTypes.HUMIDITY)) {
        return AlarmTypes.HUMIDITY;
    }
    return AlarmTypes.INPUT
}
export async function getAlarm(id: string) {
    const alarmType = getAlarmType(id);
    return getState(getShadowName(id)).then(
        (res: any) => {
            let abstractAlarm;
            switch (alarmType) {
                case AlarmTypes.TEMPERATURE:
                    abstractAlarm = getAbstractAlarm(res, AlarmPathIdTypes.TEMPERATURE, AlarmTypes.TEMPERATURE, id);
                    break;
                case AlarmTypes.ANALOG:
                    abstractAlarm = getAbstractAlarm(res, AlarmPathIdTypes.ANALOG, AlarmTypes.ANALOG);
                    break;
                case AlarmTypes.HUMIDITY:
                    abstractAlarm = getAbstractAlarm(res, AlarmPathIdTypes.HUMIDITY, AlarmTypes.HUMIDITY, id);
                    break;
                case AlarmTypes.INPUT:
                default:
                    abstractAlarm = getAbstractAlarm(res, AlarmPathIdTypes.INPUT, AlarmTypes.INPUT, id);
                    break;
            }
            return Promise.resolve(abstractAlarm);
        }
    )
}

export async function updateAlarms(data: any, id: string, status: boolean) {
    const alarmType = getAlarmType(id);
    switch (alarmType) {
        case AlarmTypes.TEMPERATURE:
            return postState(createAlarmToUpdate(data, status, AlarmTypes.TEMPERATURE, AlarmPathIdTypes.TEMPERATURE, id), getShadowName(id));
        case AlarmTypes.HUMIDITY:
            return postState(createAlarmToUpdate(data, status, AlarmTypes.HUMIDITY, AlarmPathIdTypes.HUMIDITY, id), getShadowName(id));
        case AlarmTypes.ANALOG:
            return postState(createAlarmToUpdate(data, status, AlarmTypes.ANALOG, AlarmPathIdTypes.ANALOG), getShadowName(id));
        case AlarmTypes.INPUT:
        default:
            return postState(createAlarmToUpdate(data, status, AlarmTypes.INPUT, AlarmPathIdTypes.INPUT, id), getShadowName(id));
    }
}
const createAlarmToUpdate = (data: any, status: boolean, alarmType: AlarmTypes, alarmPathId: AlarmPathIdTypes, id?: string) => {
    const key = id ? id.replace(alarmPathId, '') : '';
    if (alarmType === AlarmTypes.INPUT) {
        return {
            [AlarmTypes.INPUT + key + 'SettingsId']: {
                'WSett': {
                    'ReactTime': convertZeroToOne(convertTimeToMinutesAndSeconds(data, 'reactionTime')),
                },
                'ONumber': 10,
                'Active': status,
                'TSource': getTriggerSource(AlarmTypes.INPUT, key)
            }
        }
    }
    return {
        [alarmType + key + 'SettingsId']: {
            'Active': status,
            'ONumber': 10,
            'TSource': getTriggerSource(alarmType, key),
            'WSett': {
                'Hys': parseFloat(_.get(data, 'hysteresis', 0)),
                'ReactTime': convertZeroToOne(convertTimeToMinutesAndSeconds(data, 'reactionTime')),
                'valueUp': parseInt(_.get(data, 'valueUp', 0), 10),
                'valueDown': parseInt(_.get(data, 'valueDown', 0), 10),
            }
        }
    }
}

const getTriggerSource = (alarmType: AlarmTypes, key: string) => {
    if (alarmType === AlarmTypes.INPUT) {
        return parseInt(key, 10) - 1
    } else if (alarmType === AlarmTypes.TEMPERATURE) {
        return parseInt(key, 10) + 5;
    } else if (alarmType === AlarmTypes.HUMIDITY) {
        return 12;
    } else {
        return 14;
    }
}